import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Hourglass, LogOut, Shield, Send } from 'lucide-react';
import API, { extractErrorMessage } from '../lib/api';
import { toast } from 'sonner';

const ROLES = [
  { value: 'cardholder', label: 'Cardholder' },
  { value: 'finance_reviewer', label: 'Finance Reviewer' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'finance_admin', label: 'Finance Admin' },
];

/**
 * Landing page for SSO users who've authenticated but haven't been granted a
 * role yet (role='pending'). They can submit an access request (desired role +
 * reason) which a Finance Admin reviews.
 */
export default function PendingActivationPage() {
  const { user, loading, logout } = useAuth();
  const [existing, setExisting] = useState(undefined); // undefined=loading, null=none
  const [requestedRole, setRequestedRole] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading || !user || user.role !== 'pending') return;
    API.get('/access-requests/mine')
      .then((res) => setExisting(res.data?.request || null))
      .catch(() => setExisting(null));
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'pending') return <Navigate to="/dashboard" replace />;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await API.post('/access-requests', {
        requested_role: requestedRole || null,
        reason,
      });
      setExisting(res.data?.request || { status: 'pending' });
      toast.success('Access request submitted');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  const pending = existing && existing.status === 'pending';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-5">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <Hourglass size={26} className="text-amber-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Awaiting access</h1>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Hi {user.name || 'there'} — we've recognised you via the Pam Golding CRM,
              but a Finance Admin still needs to grant you access.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-left text-xs text-slate-600 space-y-1">
          <div className="flex items-center gap-2 font-medium text-slate-700">
            <Shield size={12} /> Your identifiers
          </div>
          {user.masterPersKey && (
            <div><span className="text-slate-400">masterPersKey:</span> {user.masterPersKey}</div>
          )}
          {user.email && (
            <div><span className="text-slate-400">Account ID:</span> {user.email}</div>
          )}
        </div>

        {existing === undefined ? (
          <p className="text-xs text-slate-400 text-center">Loading…</p>
        ) : pending ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 text-center">
            Your access request is <strong>pending review</strong>. You'll be signed in once a
            Finance Admin approves it.
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Requested role (optional)</label>
              <Select value={requestedRole} onValueChange={setRequestedRole}>
                <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Let the admin decide" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Reason (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why do you need access?"
                className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm"
              />
            </div>
            <Button className="w-full h-9 text-sm" disabled={submitting} onClick={submit} data-testid="request-access-submit">
              <Send size={14} className="mr-1.5" /> {submitting ? 'Submitting…' : 'Request access'}
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button variant="outline" className="flex-1 h-9 text-sm" onClick={() => window.location.reload()}>
            Check access
          </Button>
          <Button
            variant="outline"
            className="flex-1 h-9 text-sm"
            onClick={async () => { await logout(); window.location.href = '/login'; }}
            data-testid="pending-logout-button"
          >
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
